import { Grid, useMediaQuery } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { CarCardType, IPromotionCard } from '~/data/types/car/cars';
import {
  ISearchUsedCarsQuery,
  UsedCarType,
} from '~/data/types/usedCar/usedCarData';
import { useAppDispatch, useAppSelector } from '~/redux-toolkit/store/hooks';
import {
  setIds,
  setLeaseIds,
  setUsedCarIds,
} from '~/redux-toolkit/slices/wishlistSlice';
import { RootState } from '~/redux-toolkit/store/store';
import { ROUTES } from '~/data/routes';
import CarCard from '~/components/CarCard';
import UsedCarCard from '~/components/UsedCarCard/';
import PromotionCard from '~/components/PromotionCard';
import {
  cardContainer,
  cardsContainerSeo,
} from '~/styles/pages/ResultsPage/useStyles';
import { useTheme } from '@mui/system';
import InfoBanner from '~/components/InfoBanner';
import { iconStyleDesktopListing } from '~/styles/pages/lobbyPage/useStyles';
import useSelectFilters from '~/hooks/useSelectFilters';
import useGetSeoContent from '~/hooks/useGetSeoContent';
import { ISeoResutlsPage } from '~/data/types/seo/seoResutlsPages';
import { useIsLeaseCar } from './useIsLeaseCar';
import FamilyCarPromoCard from '~/components/FamilyCarPromoCard/FamilyCarPromoCard';
import { useIsNewCar } from './useIsNewCar';
import { ISearchCarsQuery, OptionsType } from '~/data/types/resultPageType';
import { SearchType } from './useGenerateSeoTexts';

interface useGetCarsAndBannersProps {
  cars: CarCardType[] | UsedCarType[];
  type: string;
  seoContent?: ISeoResutlsPage;
  isV2?: boolean;
  categoryInfo?: {
    categories: OptionsType;
    query: ISearchCarsQuery | ISearchUsedCarsQuery;
    searchType: SearchType;
  };
}

const useGetCarsAndBanners = ({
  cars,
  type,
  seoContent,
  isV2 = false,
  categoryInfo,
}: useGetCarsAndBannersProps) => {
  const isLease = useIsLeaseCar();
  const isNewCar = useIsNewCar();
  const { ids, usedCarIds, leaseIds } = useAppSelector(
    (state: RootState) => state.wishlist,
  );

  const { promotionCards, familyCarsPromo } = useAppSelector(
    (state: RootState) => state.resultPage,
  );
  const filters = useSelectFilters(type);

  const { shouldDisplay, seoBannerDescription, seoBannerTitle } =
    useGetSeoContent({
      propertyKeys: ['brand', 'model'],
      propertyValues: [filters.brands, filters.models],
      seoContent,
      cars,
      type,
    });

  const dispatch = useAppDispatch();
  const onWishlistButtonClickHandler = useCallback(
    (id: number) => {
      if (isLease) {
        dispatch(setLeaseIds({ newId: id }));
      } else {
        dispatch(setIds({ newId: id }));
      }
    },
    [dispatch, isLease],
  );
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const isSm = useMediaQuery(theme.breakpoints.between('md', 1400));
  const isMd = useMediaQuery(theme.breakpoints.between(1400, 1700));
  const isLg = useMediaQuery(theme.breakpoints.up(1700));

  const onWishlistUsedCarButtonClickHandler = useCallback(
    (id: number) => {
      dispatch(setUsedCarIds({ newId: id }));
    },
    [dispatch],
  );

  const promotionCardMap = useMemo(() => {
    const cardMap: Record<number, IPromotionCard> = {};
    const usedKeys = new Set<number>();

    promotionCards?.forEach((card) => {
      let key = card.cardSequence;

      // Generate the next available unique key
      key = [...Array.from({ length: promotionCards.length })]
        .map((_, i) => card.cardSequence + i)
        .find((newKey) => !usedKeys.has(newKey)) as number;

      usedKeys.add(key);
      cardMap[key] = card;
    });

    return cardMap;
  }, [promotionCards]);

  const checkIsFamilyCarPromoCard = useCallback(
    (index: number) => {
      const moreThanOneCondition =
        index > 0 &&
        index % (Math.floor(cars.length / familyCarsPromo.length) - 1) === 0;

      return familyCarsPromo.length === 1
        ? cars.length > 8
          ? index === 7
          : index === cars.length - 1
        : moreThanOneCondition;
    },
    [familyCarsPromo, cars.length],
  );

  const checkIsBanner = useCallback(
    (currentCards: number) => {
      const isLast = cars.length <= currentCards;

      switch (true) {
        case currentCards === 0:
          return false;
        case isXs && (currentCards === 6 || (isLast && currentCards < 6)):
          return true;
        case isSm && (currentCards === 8 || (isLast && currentCards < 8)):
          return true;
        case isMd && (currentCards === 12 || (isLast && currentCards < 12)):
          return true;
        case isLg && (currentCards === 12 || (isLast && currentCards < 12)):
          return true;

        default:
          return false;
      }
    },
    [cars.length, isLg, isMd, isSm, isXs],
  );

  const categoryInfoBanner = useMemo(() => {
    if (!categoryInfo) {
      return null;
    }
    const { categories: filteredCategories = [] } =
      categoryInfo?.query?.filters || {};

    const chosenCategory = categoryInfo?.categories?.values?.find(
      (cat) => cat?.title === filteredCategories?.[0],
    );

    const seoData = chosenCategory?.seo?.page?.find(
      (page) => page.searchType === categoryInfo?.searchType,
    );

    if (!seoData || filteredCategories?.length !== 1) {
      return null;
    }

    return (
      <Grid sx={{ ...cardContainer, ...cardsContainerSeo }} item xs={12}>
        <InfoBanner
          description={seoData.contentBannerDescription as string}
          title={seoData.contentBannerTitle}
          shouldDisplayTitle={false}
          iconStyle={iconStyleDesktopListing}
        />
      </Grid>
    );
  }, [categoryInfo]);

  const getMappedCars = useCallback(() => {
    const newRPCardsArray: JSX.Element[] = [];
    cars?.forEach((car: UsedCarType | CarCardType, index: number) => {
      if (promotionCardMap[index]) {
        newRPCardsArray.push(
          <Grid sx={cardContainer} item key={promotionCardMap[index].title}>
            <PromotionCard promotionCard={promotionCardMap[index]} />
          </Grid>,
        );
      }

      newRPCardsArray.push(
        <Grid
          sx={cardContainer}
          item
          key={car.id}
          data-car-index={index}
          tabIndex={0}
        >
          {type === ROUTES.USED_CAR ? (
            <UsedCarCard
              car={car as UsedCarType}
              isInWishlist={usedCarIds?.includes(car.id)}
              onWishlistButtonClicked={() => {
                onWishlistUsedCarButtonClickHandler(car.id);
              }}
              isV2={isV2}
            />
          ) : (
            <CarCard
              car={car as CarCardType}
              isInWishlist={ids?.includes(car.id) || leaseIds?.includes(car.id)}
              onWishlistButtonClicked={() => {
                onWishlistButtonClickHandler(car.id);
              }}
            />
          )}
        </Grid>,
      );

      if (isNewCar && checkIsFamilyCarPromoCard(index)) {
        newRPCardsArray.push(
          <Grid
            sx={cardContainer}
            item
            key={`Promo${
              Math.floor(
                index / (Math.floor(cars.length / familyCarsPromo.length) - 1),
              ) - 1
            }`}
          >
            <FamilyCarPromoCard
              familyCarPromo={
                familyCarsPromo[
                  familyCarsPromo.length === 1
                    ? 0
                    : Math.floor(
                        index /
                          (Math.floor(cars.length / familyCarsPromo.length) -
                            1),
                      ) - 1
                ]
              }
            />
          </Grid>,
        );
      }
      for (
        let index =
          familyCarsPromo.length === 1
            ? 1
            : Math.floor(cars.length / familyCarsPromo.length) - 1;
        index < familyCarsPromo.length;
        index++
      ) {
        if (familyCarsPromo[index]) {
          newRPCardsArray.push(
            <Grid
              sx={cardContainer}
              item
              key={`Promo ${familyCarsPromo[index].id}`}
            >
              <FamilyCarPromoCard familyCarPromo={familyCarsPromo[index]} />
            </Grid>,
          );
        }
      }
    });

    if (seoContent && seoBannerDescription && shouldDisplay) {
      newRPCardsArray.push(
        <Grid sx={{ ...cardContainer, ...cardsContainerSeo }} item xs={12}>
          <InfoBanner
            description={seoBannerDescription as string}
            title={seoBannerTitle}
            shouldDisplayTitle={false}
            iconStyle={iconStyleDesktopListing}
          />
        </Grid>,
      );
    } else if (categoryInfoBanner) {
      newRPCardsArray.push(categoryInfoBanner);
    }

    return newRPCardsArray;
  }, [
    leaseIds,
    cars,
    checkIsBanner,
    ids,
    isNewCar,
    onWishlistButtonClickHandler,
    onWishlistUsedCarButtonClickHandler,
    promotionCardMap,
    seoContent,
    type,
    usedCarIds,
    shouldDisplay,
    seoBannerDescription,
    seoBannerTitle,
    familyCarsPromo,
    checkIsFamilyCarPromoCard,
    isV2,
    categoryInfoBanner,
  ]);

  return getMappedCars;
};

export default useGetCarsAndBanners;
